import {
  Injectable,
  inject,
  computed,
  Signal,
  signal,
  WritableSignal,
} from "@angular/core";
import { LoggedUserService } from "@keus-template/auth";
import { map } from "rxjs/operators";
import {
  NavigationItem,
  PermissionRule,
  UserRole,
} from "../../../models/permissions";
import { NAVIGATION_CONFIG } from "../../../config/navigation.config";

@Injectable({
  providedIn: "root",
})
export class PermissionsService {
  private readonly _loggedUserService = inject(LoggedUserService);

  /** Reactive signal for user role */
  private readonly _userRole: WritableSignal<UserRole | null> = signal(null);

  /** Current user role as readonly signal */
  public readonly userRole: Signal<UserRole | null> =
    this._userRole.asReadonly();

  constructor() {
    // Subscribe to user identifiers and update role reactively
    // No localStorage - role comes from Cognito token via LoggedUserService
    this._loggedUserService
      .getUserIdentifiers$()
      .pipe(
        map((identifiers) => {
          const group = identifiers.group?.toLowerCase();
          if (group && Object.values(UserRole).includes(group as UserRole)) {
            return group as UserRole;
          }
          return null;
        }),
      )
      .subscribe((role) => {
        // Update the signal with new role
        this._userRole.set(role);
        
        // Note: Storage is now handled by AuthFlowService as single source of truth
        // No need to duplicate storage here
      });
  }


  /**
   * Navigation configuration imported from external config
   */
  private readonly _navigationConfig = NAVIGATION_CONFIG;

  /**
   * Get allowed navigation items for current user
   */
  public readonly allowedNavigation: Signal<NavigationItem[]> = computed(() => {
    const role = this.userRole();
    if (!role) return [];

    return this._navigationConfig.filter((item) =>
      this._hasPermission(item.permission, role),
    );
  });

  /**
   * Check if user has permission for a specific rule
   */
  public hasPermission(permission: PermissionRule): boolean {
    const role = this.userRole();
    if (!role) return false;

    return this._hasPermission(permission, role);
  }

  /**
   * Check if user can access a specific route
   */
  public canAccessRoute(path: string): boolean {
    const role = this.userRole();
    if (!role) return false;

    const item = this._navigationConfig.find((nav) => nav.path === path);
    if (!item) return false;

    return this._hasPermission(item.permission, role);
  }


  /**
   * Internal method to check permission
   */
  private _hasPermission(permission: PermissionRule, role: UserRole): boolean {
    // Check if role is allowed
    if (!permission.roles.includes(role)) {
      return false;
    }

    // Check additional condition if present
    if (permission.condition) {
      return permission.condition();
    }

    return true;
  }

  /**
   * Manually refresh user role from identifiers (used when needed)
   */
  public refreshUserRole(): void {
    this._loggedUserService
      .getUserIdentifiers$()
      .pipe(
        map((identifiers) => {
          const group = identifiers.group?.toLowerCase();
          if (group && Object.values(UserRole).includes(group as UserRole)) {
            return group as UserRole;
          }
          return null;
        }),
      )
      .subscribe((role) => {
        this._userRole.set(role);
        // Note: Storage is now handled by AuthFlowService as single source of truth
        // No need to duplicate storage here
      });
  }
}
